import { Theme, ThemeOptions } from "@mui/material";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { colors } from "./colors";

const typography = {
  fontFamily: "'Outfit', 'Roboto', Helvetica, Arial, sans-serif",
  fontSize: 14,
  h1: {
    fontFamily: "'Montserrat', 'Roboto', sans-serif",
    fontSize: "2.1rem",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
  },
  h2: {
    fontFamily: "'Montserrat', 'Roboto', sans-serif",
    fontSize: "1.6rem",
    fontWeight: 700,
    letterSpacing: "-0.015em",
    lineHeight: 1.2,
  },
  h3: {
    fontFamily: "'Montserrat', 'Roboto', sans-serif",
    fontSize: "1.3rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    lineHeight: 1.25,
  },
  h4: {
    fontFamily: "'Montserrat', 'Roboto', sans-serif",
    fontSize: "1.1rem",
    fontWeight: 700,
    letterSpacing: "-0.005em",
  },
  h5: {
    fontFamily: "'Montserrat', 'Roboto', sans-serif",
    fontSize: "1rem",
    fontWeight: 700,
  },
  h6: {
    fontFamily: "'Montserrat', 'Roboto', sans-serif",
    fontSize: ".92rem",
    fontWeight: 700,
    letterSpacing: "0.01em",
  },
  subtitle2: {
    fontSize: ".76rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
  },
  body1: {
    fontSize: "1rem",
    fontWeight: 400,
  },
  button: {
    fontWeight: 600,
    letterSpacing: "0.01em",
    textTransform: "none" as const,
  },
};

// Softer, more diffuse shadow scale than MUI's default — brand-tinted navy
// so elevated surfaces feel like part of the same design system rather than
// generic drop shadows.
const shadowTint = (opacity: number, blur: number, spread: number) =>
  `0 ${blur}px ${blur * 2}px -${spread}px rgba(25, 52, 68, ${opacity})`;

const shadows: Theme["shadows"] = [
  "none",
  shadowTint(0.1, 6, 4),
  shadowTint(0.12, 8, 5),
  shadowTint(0.14, 10, 6),
  shadowTint(0.16, 12, 7),
  shadowTint(0.17, 14, 8),
  shadowTint(0.18, 14, 8),
  shadowTint(0.19, 16, 9),
  shadowTint(0.2, 16, 9),
  shadowTint(0.21, 18, 10),
  shadowTint(0.22, 18, 10),
  shadowTint(0.22, 20, 11),
  shadowTint(0.23, 20, 11),
  shadowTint(0.24, 22, 12),
  shadowTint(0.24, 22, 12),
  shadowTint(0.25, 24, 13),
  shadowTint(0.25, 24, 13),
  shadowTint(0.26, 26, 14),
  shadowTint(0.26, 26, 14),
  shadowTint(0.27, 28, 15),
  shadowTint(0.27, 28, 15),
  shadowTint(0.28, 30, 16),
  shadowTint(0.28, 30, 16),
  shadowTint(0.29, 32, 17),
  shadowTint(0.29, 32, 17),
] as Theme["shadows"];

const shape = { borderRadius: 12 };

const sharedComponentOverrides: ThemeOptions["components"] = {
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
        borderRadius: 14,
      },
      elevation1: {
        boxShadow: shadowTint(0.1, 6, 4),
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        borderRadius: 16,
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: 10,
        paddingInline: "1.1rem",
        transition: "transform .2s cubic-bezier(.16,1,.3,1), box-shadow .2s ease",
      },
      containedPrimary: {
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: shadowTint(0.22, 14, 8),
        },
      },
      outlined: {
        "&:hover": {
          transform: "translateY(-1px)",
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        borderRadius: 999,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 8,
        fontSize: ".72rem",
        fontWeight: 500,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontWeight: 600,
        fontSize: ".76rem",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        borderRadius: 10,
        "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.divider,
        },
      }),
      input: ({ theme }: { theme: Theme }) => ({
        "&.Mui-disabled": {
          WebkitTextFillColor: theme.palette.text.disabled,
          color: theme.palette.text.disabled,
        },
      }),
    },
  },
  // Disabled-field treatment shared by every MuiInputBase-derived control
  // (TextField, Select, etc). Targeting descendants under `&.Mui-disabled`
  // keeps this scoped to the theme (no global !important CSS needed) while
  // relying on theme.palette tokens so it stays correct in both color
  // schemes automatically.
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        "&.Mui-disabled fieldset": {
          borderColor: theme.palette.divider,
        },
        "&.Mui-disabled .MuiSelect-icon, &.Mui-disabled .MuiSelect-iconOutlined": {
          display: "none",
        },
        "&.Mui-disabled .MuiInputAdornment-root, &.Mui-disabled .MuiIconButton-root": {
          display: "none",
        },
      }),
    },
  },
  // MUI X date/time pickers register their own theme components; mirror the
  // same disabled-field treatment so pickers match plain text fields.
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        "&.Mui-disabled .MuiPickersOutlinedInput-notchedOutline, &.Mui-disabled fieldset": {
          borderColor: theme.palette.divider,
        },
      }),
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        "&.Mui-disabled .MuiPickersSectionList-root, &.Mui-disabled .MuiPickersSection-content, &.Mui-disabled .MuiPickersSectionList-separator": {
          color: theme.palette.text.disabled,
          WebkitTextFillColor: theme.palette.text.disabled,
        },
        "&.Mui-disabled .MuiPickersInputAdornment-root, &.Mui-disabled .MuiIconButton-root": {
          display: "none",
        },
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        "& .MuiButton-contained": {
          borderRadius: 999,
          paddingInline: "1.5rem",
          paddingBlock: "0.35rem",
        },
      },
    },
  },
};

// Non-palette theme options shared by both color schemes. MUI's `colorSchemes`
// option only recognizes palette-level keys, so typography/shape/shadows/
// component overrides must be spread onto the top-level `createTheme()` call
// instead of nested inside `darkTheme`/`lightTheme`.
export const themeOptions: ThemeOptions = {
  typography: typography as any,
  shape,
  shadows,
  components: sharedComponentOverrides,
};

export const darkTheme: Partial<Theme> = {
  palette: {
    mode: "dark",
    primary: {
      main: colors.sky,
      dark: colors.blue,
    },
    secondary: {
      main: "#fafafa",
    },
    info: {
      main: "#0288d1",
      contrastText: "#111111",
    },
    success: {
      main: "#1b8441",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#ed6c02",
      contrastText: "#111111",
    },
    background: {
      default: colors.navyDeep,
      paper: "#173142",
    },
    action: {
      selected: "#104253",
      hover: "rgba(72, 202, 242, 0.08)",
    },
    text: {
      primary: "rgba(255, 255, 255, 0.92)",
      secondary: "rgba(255, 255, 255, 0.64)",
      disabled: "rgba(255, 255, 255, 0.45)",
    },
    divider: "rgba(255, 255, 255, 0.1)",
  } as any,
} as any;

export const lightTheme: Partial<Theme> = {
  palette: {
    mode: "light",
    primary: {
      main: colors.blue,
      dark: colors.blueHover,
    },
    secondary: {
      main: "#444",
    },
    info: {
      main: "#0288d1",
      contrastText: "#111111",
    },
    success: {
      main: "#1b8441",
      light: "#6ee7a0",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#ed6c02",
      contrastText: "#111111",
    },
    background: {
      default: colors.bg,
      paper: colors.surface,
    },
    action: {
      selected: "#EBF9FE",
      hover: "rgba(33, 117, 178, 0.05)",
    },
    text: {
      primary: colors.navy,
      secondary: "#5c717d",
      disabled: "rgba(0, 0, 0, 0.6)",
    },
    divider: colors.line,
  } as any,
} as any;

